import { z } from 'zod';

export const INGEST_SCHEMA_VERSION = 1;

/** Limits of the ingest contract (BB-25); SDKs enforce the same numbers before sending. */
export const IngestLimits = {
  maxOperations: 100,
  maxBodyBytes: 1_048_576,
  maxNameLength: 255,
  maxPropertyKeyLength: 255,
  maxPropertiesBytes: 65_536,
  maxPropertiesDepth: 5,
  maxKeysPerObject: 256,
  maxStringLength: 2048,
  maxArrayItems: 100,
  dedupeWindowDays: 30,
  sdkMaxOperationAgeDays: 14,
  /** Device clocks within this skew are trusted as is. */
  clockSkewToleranceSeconds: 60,
  /** Raw event retention (BB-22): older events are rejected. */
  maxEventAgeDays: 395,
} as const;

/** ISO-8601 with offset, with or without milliseconds (`2026-10-16T12:00:00Z`, `…00.123+03:00`). */
export const IsoDateTime = z.iso.datetime({ offset: true });

export const EventName = z
  .string()
  .trim()
  .min(1)
  .max(255)
  .refine((n) => !n.startsWith('$'), 'reserved_name');

/** Any JSON object; depth, size and key rules are checked by the domain validator (per-operation errors). */
export const Properties = z.record(z.string(), z.json());

export const OperationStatus = z.enum(['accepted', 'duplicate', 'retryable_error', 'permanent_error']);

/** Every operation code; each ticket only switches on its own checks (table in the BB-25 plan). */
export const IngestCode = z.enum([
  // accepted with a warning
  'clock_adjusted',
  'name_aliased',
  'stale_ignored',
  'identity_unverified',
  'trusted_write_reported',
  'late_event',
  'card_dismiss_without_impression',
  // permanent errors
  'invalid_operation',
  'unknown_operation_type',
  'invalid_event_name',
  'reserved_name',
  'invalid_property_key',
  'properties_too_large',
  'properties_too_deep',
  'too_many_properties',
  'invalid_timestamp',
  'event_blocked',
  'event_test_only',
  'attribute_type_conflict',
  'invalid_attribute_value',
  'attribute_blocked',
  'trusted_event_forbidden',
  'trusted_attribute_forbidden',
  'subscription_group_not_found',
  'subscription_write_forbidden',
  'profile_not_found',
  'alias_conflict',
  'profile_erased',
  'session_conflict',
  'unknown_card',
  'unknown_delivery',
  // retryable
  'internal_error',
  'dependency_unavailable',
]);
export type IngestCode = z.infer<typeof IngestCode>;

export const OperationResult = z.object({
  operationId: z.uuid(),
  status: OperationStatus,
  code: IngestCode.optional(),
  message: z.string().max(500).optional(),
  retryAfterMs: z.number().int().optional(),
});
export type OperationResult = z.infer<typeof OperationResult>;

export const STANDARD_ATTRIBUTE_KEYS = ['$email', '$phone', '$first_name', '$last_name', '$language', '$country', '$timezone', '$date_of_birth'] as const;

export const AttributeKey = z
  .string()
  .min(1)
  .max(255)
  .refine((k) => !k.startsWith('$') || (STANDARD_ATTRIBUTE_KEYS as readonly string[]).includes(k), 'reserved_attribute_key')
  .refine((k) => !k.includes('.'), 'attribute_key_with_dot');

export const AttributeValue = z.union([z.string(), z.number().finite(), z.boolean(), z.array(z.string()), z.record(z.string(), z.json())]);

/** Attribute change; shapes live here, semantics in BB-26. */
export const AttributeChange = z.discriminatedUnion('op', [
  z.strictObject({ op: z.literal('set'), key: AttributeKey, value: AttributeValue, valueType: z.literal('time').optional() }),
  z.strictObject({ op: z.literal('unset'), key: AttributeKey }),
  z.strictObject({ op: z.literal('increment'), key: AttributeKey, by: z.number().finite() }),
  z.strictObject({ op: z.literal('add'), key: AttributeKey, values: z.array(z.string().max(255)).min(1).max(100) }),
  z.strictObject({ op: z.literal('remove'), key: AttributeKey, values: z.array(z.string().max(255)).min(1).max(100) }),
]);

export type AttributeChangeInput = z.infer<typeof AttributeChange>;

/** Envelope of any operation: just enough to report a per-operation result for unknown or invalid ones. */
export const OperationEnvelope = z.looseObject({ type: z.string().min(1).max(64), operationId: z.uuid() });
